import { Component, EventEmitter, forwardRef, Input, OnDestroy, Output } from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { BdcDashboardState } from './../../../store/bdcdashboard/bdcdashboard.reducer';
import * as fromDashboardSelectors from './../../../store/bdcdashboard/bdcdashboard.selectors';
import { getCarImageAbsoluteURL, setDefaultImg, unsubscribeSubscriptions } from 'src/app/modules/shared/services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { SubscriptionList } from './../../../shared/models/asp.types';

@Component({
  selector: 'app-trade-in-info',
  templateUrl: './trade-in-info.component.html',
  styleUrls: ['./trade-in-info.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TradeInInfoComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TradeInInfoComponent),
      multi: true
    }
  ]
})
export class TradeInInfoComponent implements ControlValueAccessor, OnDestroy {
  vehicles = [];
  tradeInForm: UntypedFormGroup;
  showOther = true;
  selectedGroup = 1;
  @Input() from: string;
  @Input() isTradeInEnabled: boolean;
  @Output() formEdited:EventEmitter<boolean> = new EventEmitter();
  private subs: SubscriptionList = {};
  validatedFields = ['make', 'model', 'year'];
  ownVehicleAvailable: boolean;
  setDefaultImg=setDefaultImg;
  maxManualTradeInVehicle = 3;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly dashboardState: Store<BdcDashboardState>,
    private readonly translate: TranslateService
    ) {
    this.tradeInForm = this.formBuilder.group({
      tradeInCheckbox: [false],
      vehicle: this.formBuilder.array([
       this.formBuilder.group({
        year: [null],
        make: [null],
        model: [null],
        vin: [null],
        mileage: [null],
        trim: [null],
        isOwnVehicle: [false]
      })]),
      title: [''],
      trepresentativeInfo: [''],
      tradeInComments:[''],
      dealerComments: [''],
      internalComments: [''],
      additionalComments: ['']
    });
    this.getVehicleFromGarage();
    this.subs.formChangeSub = this.tradeInForm.valueChanges.subscribe(value => {
      this.onChange(value);
      this.onTouched();
      this.formEdited.emit(!this.tradeInForm.dirty);
    });
  }

  onChange = (_value: any) => { };
  onTouched = () => { };

  get value(): any {
    return this.tradeInForm.value;
  }

  set value(value: any) {
    this.tradeInForm.patchValue(value);
    this.setTradeInVehicles(value.vehicle);
    this.onChange(value);
    this.onTouched();
  }

  checkAdditonalComment() {
    return !!this.tradeInForm.get('additionalComments')?.value;
  }

  setTradeInVehicles(value) {
    value.forEach((element, i) => {
      if (!this.vehicleArray.controls[i]) {
        this.vehicleArray.push(new UntypedFormGroup({
          year: new UntypedFormControl(element && element.year ? element.year : ''),
          make: new UntypedFormControl(element && element.make ? element.make : ''),
          model: new UntypedFormControl(element && element.model ? element.model : ''),
          trim: new UntypedFormControl(element && element.trim ? element.trim : ''),
          vin: new UntypedFormControl(element && element.vin ? element.vin : ''),
          mileage: new UntypedFormControl(element && element.mileage ? +element.mileage : 0),
          isOwnVehicle: new UntypedFormControl(element && element.isOwnVehicle ? element.isOwnVehicle : false)
        }));
      } else {
        this.vehicleArray.controls[i].patchValue(new UntypedFormGroup({
          year: new UntypedFormControl(element && element.year ? element.year : ''),
          make: new UntypedFormControl(element && element.make ? element.make : ''),
          model: new UntypedFormControl(element && element.model ? element.model : ''),
          trim: new UntypedFormControl(element && element.trim ? element.trim : ''),
          vin: new UntypedFormControl(element && element.vin ? element.vin : ''),
          mileage: new UntypedFormControl(element && element.mileage ? +element.mileage : 0),
          isOwnVehicle: new UntypedFormControl(element && element.isOwnVehicle ? element.isOwnVehicle : false)
        }));
      }
    });
  }

  get vehicleArray() {
    return this.tradeInForm.get('vehicle') as UntypedFormArray;
  }

  get f() { return this.tradeInForm.controls; }

  registerOnChange = (fn: any) => {
    this.onChange = fn;
  }

  writeValue(value: any): void {
    if (value) {
      this.value = value;
      this.setVehicleOnUpdate(value);
    }

    if (value === null) {
      this.tradeInForm.reset();
    }
  }

  setVehicleOnUpdate(value) {
    if (value.tradeInCheckbox && value.vehicle.length) {
      value.vehicle.forEach(vehicle => {
        if (this.vehicles.find(x => x.vin === vehicle.vin)) {
          const event = { checked: true };
          const data = vehicle;
          const index = this.vehicles.findIndex(x => x.vin === vehicle.vin);
          this.setVehicle(event, data, index);
        }
      })
    }
  }
  

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(_: UntypedFormControl) {
    if(this.vehicleArray.length) {
      this.vehicleArray.controls.forEach((a,i) => {
        if(this.tradeInForm.value.tradeInCheckbox) {
          this.setVehicleValidator(i);
        }
        else {
          this.removeVehicleValidator(i);
        }
      })
    }
    return this.tradeInForm.valid ? null : { tradeIn: { valid: false } };
  }

  setVehicle(event, vehicle, index) {
    delete vehicle.isChecked;
    const tradeInVehicles = this.tradeInForm.value.vehicle;
    const tradeInVehiclesLength = tradeInVehicles.length;
    const vehicleIndex = tradeInVehicles.findIndex(x => x.vin === vehicle.vin);
    if (event.checked) {
      vehicle = { ...vehicle, isOwnVehicle: true }
      this.vehicles[index].isChecked = true;
      if (vehicleIndex === -1) {
        if (tradeInVehiclesLength === 1 && (!this.vehicleArray.controls[0].value.isOwnVehicle && this.isVehicleControlHasValue())) {
          this.vehicleArray.controls[0].patchValue(vehicle);
        }
        else {
          this.vehicleArray.push(this.formBuilder.group({ ...vehicle }));
        }
      }
      else {
        this.vehicleArray.controls[vehicleIndex].patchValue(vehicle);
      }
    } else {
      this.vehicles[index].isChecked = false;
      if (vehicleIndex !== -1) {
        if (tradeInVehiclesLength === 1) {
          const vehicleObj = {
            year: null,
            make: null,
            model: null,
            vin: null,
            mileage: null,
            trim: null,
            isOwnVehicle: false
          };
          this.vehicleArray.controls[0].patchValue(vehicleObj);
        }
        else {
          this.vehicleArray.removeAt(vehicleIndex);
        }
      }
    }

    this.tradeInForm.updateValueAndValidity();
  }

  removeVehicleValidator(index: number) {
    const vehicle = this.vehicleArray.controls[index] as UntypedFormGroup;
    this.validatedFields.forEach(field => vehicle.controls[field].setErrors(null, { emitEvent: false }));
  }

  setVehicleValidator(index: number) {
    const vehicle = this.vehicleArray.controls[index] as UntypedFormGroup;
    this.validatedFields.forEach(fields => {
      if (!vehicle.controls[fields].value) {
        vehicle.controls[fields].setErrors({ required: `${fields} ${this.translate.instant('service.isRequired')}` });
      }
    });
  }

  getVehicleFromGarage() {
    this.subs.userVehicles = this.dashboardState.select(fromDashboardSelectors.selectUserVehicles)
      .subscribe(result => {
        this.ownVehicleAvailable = !!result.length;
        result.forEach(e => {
          this.vehicles.push({
            carImage: getCarImageAbsoluteURL(e.imageUrl),
            nickName: e.description,
            make: e.make,
            model: e.model,
            year: e.year,
            vin: e.vin,
            trim: e.trim || '',
            mileage: e.mileage || 0,
            isChecked: false
          });
        });
        this.setVehicleOnUpdate(this.tradeInForm.value);
      });
  }

  onAddVehicle() {
    this.vehicleArray.push(this.formBuilder.group({
      year: [null],
      make: [null],
      model: [null],
      vin: [null],
      mileage: [null],
      trim: [null],
      isOwnVehicle: [false]
    }));
  }

  onDeleteVehicle(index: number) {
    this.vehicleArray.removeAt(index);
  }

  manualEntryVehicles() {
    return this.vehicleArray.value.filter(x=> !x.isOwnVehicle);
  }

  isVehicleControlHasValue() {
    return (!this.vehicleArray.controls[0].value.make && !this.vehicleArray.controls[0].value.year && !this.vehicleArray.controls[0].value.model 
      && !this.vehicleArray.controls[0].value.vin && !this.vehicleArray.controls[0].value.trim && !+this.vehicleArray.controls[0].value.mileage)
  }

  isFirstmanualVehicle(index : number) {
    const manualEntryVehicles = this.manualEntryVehicles();
    return this.vehicleArray.value[index] === manualEntryVehicles[0];
  }

  ngOnDestroy() {
    unsubscribeSubscriptions(this.subs);
  }
}